/**
 * swimset design tokens — Horizon Break palette.
 *
 * Mirrors web/src/app.css :root tokens for use in JS contexts (ECharts
 * options, inline style bindings, color helpers). Keys preserve the
 * legacy names (navyDeep, swimmerBlue, etc.) so existing call sites
 * keep working; hex values point at Horizon Break equivalents.
 *
 * Stroke palette + hrZoneColor() are updated in Stage 2.
 */

export const COLORS = {
  // Surfaces (Horizon Break neutrals)
  navyDeep:    "#131315",   // bg-canvas
  navyMid:     "#1B1B1D",   // bg-card
  navyRaised:  "#292A2C",   // bg-raised

  // Data accents (Horizon Break)
  swimmerBlue: "#B7C7E8",   // accent-primary (SPL, pace, hero)
  skyBlue:     "#A9CAEE",   // accent-secondary (distance)
  yellow:      "#FBC02D",   // accent-yellow (duration, backstroke)
  blueGreen:   "#B7C7E8",   // collapsed to accent-primary in Horizon Break

  // Energy / alert (Horizon Break)
  coralRed:    "#FF5722",   // accent-fire (HR, peak)
  violet:      "#FF5722",   // collapsed to accent-fire

  // Text ramp (Horizon Break)
  textPrimary: "#E4E2E4",
  textMuted:   "#8F9098",
  textDim:     "#5C5C5C",
} as const;

/** Stroke identity, Horizon Break difficulty axis (cool→warm = easy→hard).
 *  Hexes hardcoded rather than read from CSS vars (JS can't reach :root cleanly
 *  at module-eval time and isn't worth a helper for one site). */
export const STROKE_COLORS: Record<string, string> = {
  // Accessibility lift (2026-06, low-vision blind-review BLOCKER): the original
  // midnight blues (#1A2A44 / #1B2232) and near-black Unknown (#0D0F14) were
  // indistinguishable from the #131315 canvas — chips read as empty boxes. Lifted
  // to clearly-visible mid-slate values that PRESERVE the cool=easy hue family
  // (free/back/kick stay blue/cool, distinct from the warm fly/breast/IM).
  // Tile text color is per-fill via contrastTextOn — max-contrast candidate
  // wins (Scott, 2026-07-02), so mid/light fills (yellow, teal, fire, violet,
  // green) take dark text and the dark slates keep light text.
  // Distinguishability nudge (2026-06-17 → 2026-06-18, Scott on-device UAT):
  // Freestyle, the old Breaststroke (#4A6A8A) and the old Kickboard (#3A4A6B) were
  // three near-identical slate-blues — confusable on the small relabel tiles. Scott
  // chose "keep Free/Back/Fly exact, nudge Breaststroke only." Breast went #4A6A8A →
  // azure (06-17) → teal #19C6BE (06-18) to step out of the blue family entirely;
  // Kickboard → violet (off the third blue). Drill + Mixed were also given their own
  // colors (green / crimson) so the non-core labels are distinct from each other.
  // These ARE the picker tile colors (FIX_TILES derives from this map), so the
  // relabel UI matches the chart.
  Freestyle:    "#3D5A82",   // stroke-free      — visible slate-blue, easiest (kept exact)
  Breaststroke: "#19C6BE",   // stroke-breast    — teal, out of the blue family (06-18; was #36A9DD azure)
  Backstroke:   "#FBC02D",   // stroke-back      — horizon yellow, first light (kept exact)
  Mixed:        "#A33D6B",   // stroke-mixed     — wine/rose, lifted for visibility (06-21; was #7C3B40)
  Butterfly:    "#FF5722",   // stroke-fly       — fireline orange, hardest (kept exact)
  Kickboard:    "#9B6FC8",   // stroke-kickboard — violet, distinct from the blues (06-21; was #8E7CC3)
  Unknown:      "#6B6E78",   // stroke-unknown   — visible neutral slate (06-21; was #4A4E57)

  // Drill-label chips (Phase 7, D-11). These are NOT HK strokes — they are
  // user-applied drill/effort labels shown in the swipe-deck palette. Hues
  // ride the same Horizon Break cool→warm difficulty axis so the palette reads
  // as one family with the stroke chips. One source of truth for chip color.
  Kick:   "#3A4A6B",   // cloud indigo, lifted to match the kickboard family
  Pull:   "#4A6A8A",   // atmospheric blue
  Drill:  "#4E9E6E",   // drill green (06-18: own color, distinct from Mixed/IM crimson)
  Scull:  "#4A6A8A",   // atmospheric blue
  IM:     "#5B2D31",   // horizon crimson (IM = mixed effort)
  Sprint: "#FF5722",   // fireline orange, hardest
};

/** Short labels used in chart legends and lap rows (matches Scott's Workout screen). */
export const STROKE_ABBREV: Record<string, string> = {
  Freestyle: "FREE",
  Backstroke: "BK",
  Breaststroke: "BR",
  Butterfly: "FLY",
  Mixed: "MIX",
  Kickboard: "KICK",
  Unknown: "?",

  // Drill-label chips (Phase 7, D-11) — full-word abbrevs so the palette chips
  // read clearly under a wet thumb.
  Kick: "KICK",
  Pull: "PULL",
  Drill: "DRILL",
  Scull: "SCULL",
  IM: "IM",
  Sprint: "SPRINT",
};

/** Two/three-letter chip text for the Club set cards (j4 mockup): a 29px chip
 *  holds "Fly" comfortably and "FREE" does not. STROKE_ABBREV stays the legend /
 *  lap-row vocabulary; this map is the chip-sized one. */
export const STROKE_CHIP_SHORT: Record<string, string> = {
  Freestyle: "Fr",
  Backstroke: "Ba",
  Breaststroke: "Br",
  Butterfly: "Fly",
  Kickboard: "K",
  Mixed: "Mx",
  Unknown: "?",
};

export function strokeChipShort(style: string | null | undefined): string {
  return STROKE_CHIP_SHORT[style ?? "Unknown"] ?? "?";
}

/** The word ON a coloured stroke tile, chip, or band: one register, whole
 *  words (the share card's VP round 1 found "BK" read as "back kick").
 *  Unknown keeps the app's "?"; a drill label is itself, upper-cased. Lives
 *  beside STROKE_COLORS so a tile's colour and its word have one home — the
 *  share card (chipWord) and the Sets tab strip draw from this map. */
export function strokeWord(stroke: string): string {
  const words: Record<string, string> = {
    Freestyle: "FREE",
    Backstroke: "BACK",
    Breaststroke: "BREAST",
    Butterfly: "FLY",
    Kickboard: "KICK",
    Mixed: "MIXED",
    Unknown: "?",
  };
  return words[stroke] ?? stroke.toUpperCase();
}

export function strokeColor(style: string | null | undefined): string {
  return STROKE_COLORS[style ?? "Unknown"] ?? COLORS.textDim;
}

/** Relative luminance per the standard sRGB formula (WCAG: linearize each
 *  channel, then 0.2126R + 0.7152G + 0.0722B). Shared by contrastTextOn()
 *  for both the fill and the two dark/light text candidates. */
function relativeLuminance(hex: string): number {
  let h = hex.startsWith("#") ? hex.slice(1) : hex;
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const lin = (byte: number): number => {
    const s = byte / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const r = lin(parseInt(h.slice(0, 2), 16));
  const g = lin(parseInt(h.slice(2, 4), 16));
  const b = lin(parseInt(h.slice(4, 6), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two relative luminances: (L1 + 0.05) / (L2 + 0.05)
 *  with L1 the lighter of the pair, so the ratio is always >= 1. */
function contrastRatio(lumA: number, lumB: number): number {
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Text color that stays readable ON a given fill — for labels rendered on top
 * of strokeColor()/effortColor() chips, bands, and badges. Near-black on the
 * midnight stroke blues (#1A2A44, #1B2232) is unreadable; this picks per-fill.
 *
 * Computes the actual WCAG contrast ratio of the fill against BOTH text
 * candidates (COLORS.navyDeep, COLORS.textPrimary) and returns whichever
 * wins — not a bare luminance threshold. The prior >= 0.5 threshold put
 * mid-luminance teal/green fills (Breaststroke, Drill) on the wrong side of
 * the line, choosing the lower-contrast candidate (2026-07-01 persona-panel
 * accessibility blocker, B3). Ratio comparison fixes every call site by
 * construction: it always picks the candidate with the higher measured ratio.
 *
 * Scott, 2026-07-02: max-contrast text everywhere (overrides 2026-06-11
 * white-on-fire call; readability wins for this owner). Fire/crimson fills
 * (#FF5722) now take dark text because dark measures higher contrast on them.
 *
 * Unparseable input falls back to light text (the safe default on a dark UI).
 */
export function contrastTextOn(hex: string): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return COLORS.textPrimary;
  let h = m[1];
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const bgLuminance = relativeLuminance(h);
  const darkRatio = contrastRatio(bgLuminance, relativeLuminance(COLORS.navyDeep));
  const lightRatio = contrastRatio(bgLuminance, relativeLuminance(COLORS.textPrimary));
  return darkRatio >= lightRatio ? COLORS.navyDeep : COLORS.textPrimary;
}

export function strokeAbbrev(style: string | null | undefined): string {
  return STROKE_ABBREV[style ?? "Unknown"] ?? "?";
}

/** HR-zone color, Horizon Break ramp: cool atmospheric → fire as ratio climbs.
 *  Peak shares fireline with hard; the peak halo lives in --shadow-glow-peak,
 *  applied at the component level, not encoded in the color. */
export function hrZoneColor(bpm: number, peakBpm: number): string {
  const ratio = peakBpm > 0 ? bpm / peakBpm : 0;
  if (ratio >= 0.92) return "#FF5722";   // peak     (accent-fire)
  if (ratio >= 0.85) return "#FF5722";   // hard     (accent-fire)
  if (ratio >= 0.75) return "#5B2D31";   // tempo    (accent-crimson)
  if (ratio >= 0.65) return "#FBC02D";   // aerobic  (accent-yellow)
  return "#4A6A8A";                      // easy     (sky-atmo)
}
